import { twMerge } from 'tailwind-merge';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useGetOsName } from '@hooks/useGetOsName';
import { AppDownloadUrlSourceType, getAppDownloadUrl } from '@domain/routes/getAppDownloadUrl';
import { ExternalLink } from '../../../../Clickables/ExternalLink/ExternalLink';
import { TagContent } from '../../../../TagContent/TagContent';
import { Text } from '../../../../Text/Text';
import { PostViewOptions } from '../../../constants/constants';
import { getDisplayUserDetails } from '../../../helpers/getDisplayUserDetails';
import { Post } from '../../../types/Post';
import { PostComment } from '../../PostComment/PostComment';
import { PostComments } from '../../PostComments/PostComments';
import { usePostComments } from '../../PostComments/usePostComments';
import { PostsViewerSection } from '../../PostsViewerSection/PostsViewerSection';

interface PostCommentsViewProps {
  posts: Post[];
  currentPostId: string | undefined;
  className?: string;
  onBackButtonClick: () => void;
  onViewChange: (viewName: PostViewOptions, viewCatchId?: string) => void;
}

export const PostCommentsView = ({
  posts,
  currentPostId,
  className,
  onBackButtonClick: handleBackButtonClick,
  onViewChange: handleViewChange,
}: PostCommentsViewProps) => {
  const { currentPage } = useAnalytics();
  const postCommentsData = usePostComments(posts, currentPostId);
  const { postCommentContent, postCommentDate, postComments } = postCommentsData;
  const { displayEntity, isLoading, likedByCurrentUser, likesCount } = postCommentsData;
  const { userAvatarUrl, userNickname, userId } = postCommentsData;

  const displayUserDetails = getDisplayUserDetails(
    displayEntity,
    userId,
    userNickname,
    userAvatarUrl,
  );

  const osName = useGetOsName();
  const fishbrainAppUrl = getAppDownloadUrl({
    appDownloadSourceType: AppDownloadUrlSourceType.PostsViewer,
    appDownloadSource: 'comments-bottom',
    currentPageId: currentPage,
    osName,
  });

  const handleNotLoggedIn = (): void => {
    handleViewChange('registerWallView');
  };

  const handleLikesButtonClick = (): void => {
    handleViewChange('registerWallView');
  };

  const handleDownloadAppClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'download',
        source: 'post-viewer-bottom',
        page: currentPage,
      },
    });
  };

  return (
    <PostsViewerSection
      className={twMerge('flex flex-col justify-between gap-y-4 sm:gap-y-8', className)}
      isLoading={isLoading}
      navigationLocationName="Comments"
      onBackButtonClick={handleBackButtonClick}
    >
      <div className="flex shrink-0 flex-col px-4 pt-2 pb-0 sm:px-11 sm:pt-0">
        <PostComment
          avatarUrl={displayUserDetails.displayUserAvatarUrl}
          className="pb-4"
          date={postCommentDate}
          onNotLoggedIn={handleNotLoggedIn}
          onLikesButtonClick={handleLikesButtonClick}
          likeCount={likesCount}
          likedByCurrentUser={likedByCurrentUser}
          nickname={displayUserDetails.displayUserNickname}
        >
          <Text variant="none" className="text-left break-words whitespace-pre-wrap">
            <TagContent
              id={displayUserDetails.displayUserId}
              text={postCommentContent}
              url="/tags/"
            />
          </Text>
        </PostComment>
        <hr className="border-twilight" />
      </div>
      <div className="flex flex-auto flex-col overflow-y-scroll px-4 sm:px-11">
        <PostComments
          postComments={postComments}
          className="sm:gap-y-8"
          onViewChange={handleViewChange}
        />
      </div>
      <div className="shrink-0">
        <hr className="border-twilight" />
        <div className="body-text-md p-4">
          <ExternalLink
            className="text-tuna-shade font-semibold"
            href={fishbrainAppUrl}
            onClick={handleDownloadAppClick}
            variant="textNone"
          >
            Get the Fishbrain app
          </ExternalLink>{' '}
          to comment
        </div>
      </div>
    </PostsViewerSection>
  );
};
